<template>
  <div class="container-fluid">
    <div class="row mb-5">
      <h1>{{ salesOrder.seq_number }}</h1>
    </div>

    <div class="row mb-5">
      <div class="col-12">
        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.CREATED_AT") }}</dt>
          <dd class="col-sm-8">
            {{ $timeZoneDateFormat(salesOrder.created_at) }}
          </dd>
        </dl>
        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.UPDATED_AT") }}</dt>
          <dd class="col-sm-8">
            {{ $timeZoneDateFormat(salesOrder.updated_at) }}
          </dd>
        </dl>

        <!-- <dl class="row">
          <dt class="col-sm-4">{{ $t("SALES_ORDERS.EXPIRATION_TIME") }}</dt>
          <dd class="col-sm-8">
            {{ $timeZoneDateFormat(salesOrder.expiration_time) }}
          </dd>
        </dl> -->

        <hr class="my-4" />
        <dl class="row">
          <dt class="col-sm-4">
            <h2>{{ $t("SALES_ORDERS.SENT_BY") }}</h2>
          </dt>
          <dd class="col-sm-8" v-if="salesOrder.sentBy">
            {{ $objectDenomination(salesOrder.sentBy) }}
          </dd>
        </dl>

        <dl class="row" v-if="hasSentRecipients">
          <dt class="col-sm-4">
            {{ $t("SALES_ORDERS.SENT_TO") }}
          </dt>
          <dd class="col-sm-8">
            <div v-if="sentToList.length">
              <strong>{{ $t("SALES_ORDERS.TO") }}:</strong>
              <div>
                <span
                  v-for="(item, idx) in sentToList"
                  :key="'to-' + idx"
                >
                  <a
                    :href="`mailto:${
                      typeof item === 'string' ? item : item.email
                    }`"
                  >
                    {{ typeof item === "string" ? item : item.email }} </a
                  ><span v-if="idx !== sentToList.length - 1"
                    >,
                  </span>
                </span>
              </div>
            </div>

            <div
              v-if="sentCcList.length"
              class="mt-2"
            >
              <strong>{{ $t("SALES_ORDERS.CC") }}:</strong>
              <div>
                <span
                  v-for="(item, idx) in sentCcList"
                  :key="'cc-' + idx"
                >
                  <a
                    :href="`mailto:${
                      typeof item === 'string' ? item : item.email
                    }`"
                  >
                    {{ typeof item === "string" ? item : item.email }}
                  </a>
                  <span v-if="idx !== sentCcList.length - 1"
                    >,
                  </span>
                </span>
              </div>
            </div>

            <div
              v-if="sentBccList.length"
              class="mt-2"
            >
              <strong>{{ $t("SALES_ORDERS.BCC") }}:</strong>
              <div>
                <span
                  v-for="(item, idx) in sentBccList"
                  :key="'bcc-' + idx"
                >
                  <a
                    :href="`mailto:${
                      typeof item === 'string' ? item : item.email
                    }`"
                  >
                    {{ typeof item === "string" ? item : item.email }}</a
                  >
                  <span v-if="idx !== sentBccList.length - 1"
                    >,
                  </span>
                </span>
              </div>
            </div>
          </dd>
        </dl>
        <template v-if="firstSentRow">
          <dl class="row">
            <dt class="col-sm-4">
              {{ $t("SALES_ORDERS.FIRST_SENT_AT") }}
            </dt>
            <dd class="col-sm-8">
              {{ $timeZoneDateFormat(firstSentRow.occurred_at) }}
              <span v-if="firstSentRow.user_name" class="text-muted">
                — {{ firstSentRow.user_name }}
              </span>
            </dd>
          </dl>
          <dl class="row">
            <dt class="col-sm-4">
              {{ $t("SALES_ORDERS.CLIENT_REMINDERS") }}
            </dt>
            <dd class="col-sm-8">
              <div v-for="row in reminderRows" :key="'reminder-' + row.index">
                {{ $timeZoneDateFormat(row.occurred_at) }}
                <span class="text-muted">
                  — {{ sendingRowLabel(row) }}
                </span>
              </div>
              <span v-if="!reminderRows.length">{{ $t("COMMON.NONE") }}</span>
            </dd>
          </dl>
          <dl class="row">
            <dt class="col-sm-4"></dt>
            <dd class="col-sm-8">
              <a href="#" @click.prevent="showSendingHistory = !showSendingHistory">
                <i
                  class="fas"
                  :class="showSendingHistory ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
                {{ $t("SALES_ORDERS.SENDING_HISTORY") }} ({{ sendingRows.length }})
              </a>
              <ul v-if="showSendingHistory" class="list-unstyled mt-2 mb-0">
                <li
                  v-for="row in sendingRows"
                  :key="'sending-' + row.index"
                  class="mb-2"
                >
                  <strong>{{ $timeZoneDateFormat(row.occurred_at) }}</strong>
                  — {{ sendingRowLabel(row) }}
                  <div
                    v-if="row.recipientsLabel"
                    class="text-muted small"
                  >
                    {{ row.recipientsLabel }}
                  </div>
                </li>
              </ul>
            </dd>
          </dl>
        </template>
        <dl class="row" v-else-if="salesOrder.sent_at">
          <dt class="col-sm-4">
            {{ $t("SALES_ORDERS.SENT_AT") }}
          </dt>
          <dd class="col-sm-8">
            {{ $timeZoneDateFormat(salesOrder.sent_at) }}
          </dd>
        </dl>

        <hr class="my-4" v-if="showClientValidationSection" />
        <dl class="row" v-if="showClientValidationSection">
          <dt class="col-sm-4">
            <h2>{{ $t("SALES_ORDERS.CLIENT_VALIDATION") }}</h2>
          </dt>
          <dd class="col-sm-8" v-if="salesOrder.status != ORDER_STATUS_DRAFT">
            <div class="d-flex align-items-center gap-2">
              <icon-check
                v-if="clientValidationRequired"
                :checked="salesOrder.client_validated_status === 'VALIDATED'"
              />
              <span>
                {{
                  clientValidationLabel
                }}
                {{
                  clientValidationRequired &&
                  salesOrder.client_validated_status === "VALIDATED"
                    ? $t(`SALES_ORDERS.THE`) +
                      $timeZoneDateFormat(salesOrder.client_validated_at)
                    : ""
                }}
              </span>
            </div>
          </dd>
        </dl>
        <dl
          class="row"
          v-if="
            showClientValidationSection &&
            (
              salesOrder.client_validated_by_firstname ||
              salesOrder.client_validated_by_lastname
            )
          "
        >
          <dt class="col-sm-4">
            {{ $t("COMMON.NAME") }}
          </dt>
          <dd class="col-sm-8">
            {{
              [
                salesOrder.client_validated_by_firstname,
                salesOrder.client_validated_by_lastname,
              ]
                .filter(Boolean)
                .join(" ")
            }}
          </dd>
        </dl>

        <dl class="row" v-if="showClientValidationSection && salesOrder.client_validated_by_email">
          <dt class="col-sm-4">
            {{ $t("COMMON.EMAIL") }}
          </dt>
          <dd class="col-sm-8">
            <a :href="`mailto:${salesOrder.client_validated_by_email}`">
              {{ salesOrder.client_validated_by_email }}
            </a>
          </dd>
        </dl>

        <dl class="row" v-if="showClientValidationSection && salesOrder.client_validated_by_position">
          <dt class="col-sm-4">
            {{ $t("COMMON.POSITION") }}
          </dt>
          <dd class="col-sm-8">
            {{ salesOrder.client_validated_by_position }}
          </dd>
        </dl>

        <dl class="row" v-if="showClientValidationSection && salesOrder.client_validated_by_comment">
          <dt class="col-sm-4">
            {{ $t("SALES_ORDERS.COMMENT") }}
          </dt>
          <dd class="col-sm-8">
            {{ salesOrder.client_validated_by_comment }}
          </dd>
        </dl>

        <hr class="my-4" />

        <dl class="row" v-if="salesOrder.event_name">
          <dt class="col-sm-4">
            {{ $t("SALES_ORDERS.EVENT_NAME") }}
          </dt>
          <dd class="col-sm-8">
            {{ salesOrder.event_name }}
          </dd>
        </dl>

        <dl class="row" v-if="salesOrder.event_date">
          <dt class="col-sm-4">
            {{ $t("SALES_ORDERS.EVENT_DATE") }}
          </dt>
          <dd class="col-sm-8">
            {{ $timeZoneDateFormat(salesOrder.event_date, "LL") }}
          </dd>
        </dl>

        <dl class="row" v-if="showEventTime">
          <dt class="col-sm-4">
            {{ $t("SALES_ORDERS.EVENT_TIME") }}
          </dt>
          <dd class="col-sm-8">
            {{ formatTimeWithAmPm(salesOrder.event_date) }}
          </dd>
        </dl>

        <dl class="row" v-if="salesOrder.delivery_location">
          <dt class="col-sm-4">
            {{ $t("SALES_ORDERS.DELIVERY_LOCATION") }}
          </dt>
          <dd class="col-sm-8">
            <div v-html="salesOrder.delivery_location"></div>
          </dd>
        </dl>

        <dl class="row" v-if="salesOrder.purchase_order_number">
          <dt class="col-sm-4">
            {{ $t("SALES_ORDERS.PURCHASE_ORDER_NUMBER") }}
          </dt>
          <dd class="col-sm-8">
            {{ salesOrder.purchase_order_number }}
          </dd>
        </dl>

        <dl class="row" v-if="salesOrder.allergies_and_food_intolerances">
          <dt class="col-sm-4">
            {{ $t("SALES_ORDERS.ALLERGIES_AND_FOOD_INTOLERANCES") }}
          </dt>
          <dd class="col-sm-8">
            <div v-html="salesOrder.allergies_and_food_intolerances"></div>
          </dd>
        </dl>
        <hr class="my-4" />

        <dl
          class="row"
          v-if="$currentUserCan($permissions.PERM_VIEW_ANY_CAFETERIAS)"
        >
          <dt class="col-sm-4">{{ $t("COMMON.CAFETERIA") }}</dt>
          <dd class="col-sm-8">
            <cafeteria :cafeteria="salesOrder.cafeteria" />
          </dd>
        </dl>

        <dl
          class="row"
          v-if="$currentUserCan($permissions.PERM_VIEW_ANY_ESTABLISHMENTS)"
        >
          <dt class="col-sm-4">{{ $t("COMMON.ESTABLISHMENT") }}</dt>
          <dd class="col-sm-8">
            <establishment :establishment="salesOrder.establishment" />
          </dd>
        </dl>

        <dl
          class="row"
          v-if="$currentUserCan($permissions.PERM_VIEW_ANY_SERVICECENTERS)"
        >
          <dt class="col-sm-4">{{ $t("COMMON.SERVICECENTER") }}</dt>
          <dd class="col-sm-8">
            <service-center :serviceCenter="salesOrder.serviceCenter" />
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.LOCATIONS") }}</dt>
          <dd class="col-sm-8">
            <locations :locations="salesOrder.allowedLocations" />
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.ORGANIZATION") }}</dt>
          <dd class="col-sm-8">
            <organization :organization="salesOrder.organization" />
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.STATUS") }}</dt>
          <dd class="col-sm-8">
            <sales-order-status-badge
              :salesOrder="salesOrder"
              :advanced="true"
            />
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.HAS_TAXES") }}</dt>
          <dd class="col-sm-8">
            <icon-check :checked="!salesOrder.has_no_taxes" />
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.PAYMENT_TERMS") }}</dt>
          <dd class="col-sm-8">
            <object-link :object="salesOrder.paymentTerm" />
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.WAREHOUSE") }}</dt>
          <dd class="col-sm-8">
            <object-link
              v-if="!!salesOrder.sourceWarehouse"
              :object="salesOrder.sourceWarehouse"
            />
            <span v-if="!salesOrder.sourceWarehouse">
              {{ $t("COMMON.NONE") }}
            </span>
          </dd>
        </dl>

        <dl class="row" v-if="salesOrder.salesInvoice">
          <dt class="col-sm-4">{{ $t("COMMON.SALES_INVOICE") }}</dt>
          <dd class="col-sm-8">
            <object-link :object="salesOrder.salesInvoice" />
            <sales-invoice-status-badge
              :salesInvoice="salesOrder.salesInvoice"
            />
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.RECIPIENT") }}</dt>
          <dd class="col-sm-8">
            <object-link :object="salesOrder.recipient" />
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.TOTAL") }}</dt>
          <dd class="col-sm-8" v-if="salesOrder.pricing">
            <span> {{ $formatCurrency(salesOrder.pricing.total) }} </span>
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4" v-if="salesOrder.excerpt">
            {{ $t("COMMON.NOTE") }}
          </dt>
          <dd class="col-sm-8" v-if="salesOrder.excerpt">
            <div v-html="salesOrder.excerpt"></div>
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4" v-if="salesOrder.terms_conditions">
            {{ $t("COMMON.TERMS_CONDITIONS") }}
          </dt>
          <dd class="col-sm-8" v-if="salesOrder.terms_conditions">
            <div v-html="salesOrder.terms_conditions"></div>
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4" v-if="salesOrder.description">
            {{ $t("COMMON.DESCRIPTION") }}
          </dt>
          <dd class="col-sm-8" v-if="salesOrder.description">
            <div v-html="salesOrder.description"></div>
          </dd>
        </dl>

        <div class="col-12 text-center mb-5 mt-5">
          <h2>{{ $t("COMMON.BILLING_ADDRESS") }}</h2>
        </div>

        <dl class="row" v-if="salesOrder.billing_entity_type != 'N/A'">
          <dt class="col-sm-4">{{ $t("COMMON.LASTNAME") }}</dt>
          <dd class="col-sm-8">
            {{ salesOrder.billing_lastname }}
          </dd>
        </dl>

        <dl class="row" v-if="salesOrder.billing_entity_type != 'N/A'">
          <dt class="col-sm-4">{{ $t("COMMON.FIRSTNAME") }}</dt>
          <dd class="col-sm-8">
            {{ salesOrder.billing_firstname }}
          </dd>
        </dl>

        <dl class="row" v-if="salesOrder.billing_entity_type != 'N/A'">
          <dt class="col-sm-4">{{ $t("COMMON.COMPANY_NAME") }}</dt>
          <dd class="col-sm-8">
            {{ salesOrder.billing_company_name }}
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.COUNTRY") }}</dt>
          <dd class="col-sm-8">
            {{ salesOrder.billing_country }}
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.STATE") }}</dt>
          <dd class="col-sm-8">
            {{ salesOrder.billing_state }}
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.CITY") }}</dt>
          <dd class="col-sm-8">
            {{ salesOrder.billing_city }}
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.ZIPCODE") }}</dt>
          <dd class="col-sm-8">
            {{ salesOrder.billing_zipcode }}
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.ADDRESS") }}</dt>
          <dd class="col-sm-8">
            {{ salesOrder.billing_address }}
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.EMAIL") }}</dt>
          <dd class="col-sm-8">
            {{ salesOrder.billing_email }}
          </dd>
        </dl>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import {
  BILLING_ENTITY_TYPE_INDIVIDUAL,
  BILLING_ENTITY_TYPE_COMPANY,
} from "@/constants/billingInformations";
import Cafeteria from "@/components/Cafeteria.vue";
import Establishment from "@/components/Establishment.vue";
import ServiceCenter from "@/components/ServiceCenter.vue";
import IconCheck from "@/components/IconCheck.vue";
import SalesInvoiceStatusBadge from "../../SalesInvoiceManagement/partials/SalesInvoiceStatusBadge.vue";
import SalesOrderStatusBadge from "./SalesOrderStatusBadge.vue";
import { ORDER_STATUS_DRAFT } from "../../../../../constants/orders";
import {
  salesOrderRequiresClientValidation,
  salesOrderHasEventTime,
} from "@/constants/pricingTypes";

export default {
  name: "sales-order-view-global",

  components: {
    SalesOrderStatusBadge,
    SalesInvoiceStatusBadge,
    Cafeteria,
    Establishment,
    ServiceCenter,
    IconCheck,
  },

  props: ["salesOrder"],

  data() {
    return {
      BILLING_ENTITY_TYPE_INDIVIDUAL: BILLING_ENTITY_TYPE_INDIVIDUAL,
      BILLING_ENTITY_TYPE_COMPANY: BILLING_ENTITY_TYPE_COMPANY,
      ORDER_STATUS_DRAFT: ORDER_STATUS_DRAFT,
      showSendingHistory: false,
    };
  },

  computed: {
    /**
     * Journal des envois, du plus ancien au plus récent. Un envoi (SENT) est
     * qualifié selon sa place : premier envoi, renvoi, ou envoi de la
     * révision s'il suit une remise en brouillon.
     */
    sendingRows() {
      const sendings = Array.isArray(this.salesOrder.sendings)
        ? this.salesOrder.sendings
        : [];
      let sentCount = 0;
      let afterRevert = false;

      return sendings.map((sending, index) => {
        let kind = sending.type;
        if (sending.type === "SENT") {
          if (sentCount === 0) {
            kind = "FIRST_SENT";
          } else {
            kind = afterRevert ? "REVISION_SENT" : "RESENT";
          }
          sentCount += 1;
          afterRevert = false;
        } else if (sending.type === "REVERTED_TO_DRAFT") {
          afterRevert = true;
        }

        return {
          ...sending,
          index,
          kind,
          recipientsLabel: this.formatRecipients(sending.recipients),
        };
      });
    },

    firstSentRow() {
      return this.sendingRows.find((row) => row.kind === "FIRST_SENT") || null;
    },

    reminderRows() {
      return this.sendingRows.filter((row) =>
        ["RESENT", "REVISION_SENT", "REMINDER"].includes(row.kind)
      );
    },

    normalizedSentTo() {
      const sentTo = this.salesOrder && this.salesOrder.sent_to;

      if (!sentTo) {
        return { to: [], cc: [], bcc: [] };
      }

      if (typeof sentTo === "string" || Array.isArray(sentTo)) {
        return {
          to: this.normalizeRecipients(sentTo),
          cc: [],
          bcc: [],
        };
      }

      return {
        to: this.normalizeRecipients(sentTo.to),
        cc: this.normalizeRecipients(sentTo.cc),
        bcc: this.normalizeRecipients(sentTo.bcc),
      };
    },

    sentToList() {
      return this.normalizedSentTo.to;
    },

    sentCcList() {
      return this.normalizedSentTo.cc;
    },

    sentBccList() {
      return this.normalizedSentTo.bcc;
    },

    hasSentRecipients() {
      return (
        this.sentToList.length ||
        this.sentCcList.length ||
        this.sentBccList.length
      );
    },

    showClientValidationSection() {
      return true;
    },

    clientValidationRequired() {
      return salesOrderRequiresClientValidation(this.salesOrder);
    },

    /**
     * Aide alimentaire et coupons : la date d'évènement n'est qu'une
     * référence de période, l'heure (souvent 23:59) n'a rien à dire.
     */
    showEventTime() {
      return !!this.salesOrder.event_date && salesOrderHasEventTime(this.salesOrder);
    },

    clientValidationLabel() {
      if (!this.clientValidationRequired) {
        return this.$t("SALES_ORDERS.CLIENT_VALIDATED_STATUS_NOT_REQUIRED");
      }

      if (
        this.salesOrder.client_validated_status === "PENDING" &&
        this.salesOrder.is_revision
      ) {
        return this.$t(
          "SALES_ORDERS.CLIENT_VALIDATED_STATUS_PENDING_REVISION"
        );
      }

      return this.$t(
        `SALES_ORDERS.CLIENT_VALIDATED_STATUS_${this.salesOrder.client_validated_status}`
      );
    },
  },

  created() {},

  methods: {
    normalizeRecipients(value) {
      if (!value) {
        return [];
      }

      return Array.isArray(value) ? value : [value];
    },

    sendingRowLabel(row) {
      const label = this.$t(`SALES_ORDERS.SENDING_TYPE_${row.kind}`);
      if (!row.user_name) {
        return label;
      }

      return `${label} ${this.$t("SALES_ORDERS.SENDING_BY", {
        name: row.user_name,
      })}`;
    },

    formatRecipients(recipients) {
      if (!recipients) {
        return "";
      }

      const buckets = Array.isArray(recipients) ? { to: recipients } : recipients;
      const emails = (value) =>
        this.normalizeRecipients(value)
          .map((item) => (typeof item === "string" ? item : item.email))
          .filter(Boolean)
          .join(", ");

      return [
        ["TO", buckets.to],
        ["CC", buckets.cc],
        ["BCC", buckets.bcc],
      ]
        .map(([key, value]) => [key, emails(value)])
        .filter(([, list]) => list)
        .map(([key, list]) => `${this.$t(`SALES_ORDERS.${key}`)} : ${list}`)
        .join(" · ");
    },

    formatTimeWithAmPm(datetime) {
      if (!datetime) return '';
      const time24 = this.$timeZoneDateFormat(datetime, "HH:mm");
      const momentTime = moment(datetime);
      const hour12 = momentTime.format('hh:mm A');
      return `${time24} (${hour12})`;
    },
  },

  mounted() {},

  watch: {
    salesOrder(salesOrder) {},
  },
};
</script>
